<template>
  <div class="vertical-timeline">
    <div 
      v-for="(item, index) in items" 
      :key="index"
      class="timeline-item"
      :class="{ 
        'is-completed': item.completed,
        'is-active': item.active,
        'is-pending': !item.completed && !item.active
      }"
    >
      <!-- Icono/Checkpoint -->
      <div class="timeline-marker">
        <div class="marker-icon">
          <!-- Icono personalizado o estado por defecto -->
          <i v-if="item.icon" :class="item.icon"></i>
          <i v-else-if="item.completed" class="fas fa-check"></i>
          <i v-else-if="item.active" class="fas fa-circle"></i>
          <i v-else class="far fa-circle"></i>
        </div>
      </div>

      <!-- Línea conectora -->
      <div 
        v-if="index < items.length - 1" 
        class="timeline-line"
        :class="{ 'is-completed': item.completed }"
      ></div>

      <!-- Contenido -->
      <div class="timeline-content">
        <div class="timeline-header">
          <h4 class="timeline-title">{{ item.title }}</h4>
          <span v-if="item.date" class="timeline-date">{{ item.date }}</span>
        </div>
        
        <p v-if="item.description" class="timeline-description">
          {{ item.description }}
        </p>

        <!-- Badges/Tags -->
        <div v-if="item.tags && item.tags.length" class="timeline-tags">
          <span 
            v-for="(tag, tagIndex) in item.tags" 
            :key="tagIndex"
            class="timeline-tag"
            :style="{ backgroundColor: tag.color || '#6b20c1' }"
          >
            {{ tag.label }}
          </span>
        </div>

        <!-- Slot para contenido personalizado -->
        <div v-if="$slots[`item-${index}`]" class="timeline-custom">
          <slot :name="`item-${index}`" :item="item"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VerticalTimeline',
  props: {
    items: {
      type: Array,
      required: true,
      default: () => []
      // Estructura esperada:
      // [
      //   {
      //     title: 'Título',
      //     description: 'Descripción',
      //     date: 'Fecha',
      //     completed: true/false,
      //     active: true/false,
      //     icon: 'fas fa-check', // Opcional
      //     tags: [{ label: 'Tag', color: '#6b20c1' }] // Opcional
      //   }
      // ]
    },
    completedColor: {
      type: String,
      default: '#28a745'
    },
    activeColor: {
      type: String,
      default: '#6b20c1'
    },
    pendingColor: {
      type: String,
      default: '#ccc'
    }
  }
}
</script>

<style scoped>
.vertical-timeline {
  position: relative;
  padding: 20px 0;
}

.timeline-item {
  position: relative;
  display: flex;
  padding-bottom: 40px;
  min-height: 80px;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

/* Marcador/Checkpoint */
.timeline-marker {
  position: relative;
  flex-shrink: 0;
  width: 40px;
  display: flex;
  justify-content: center;
  z-index: 2;
}

.marker-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
  border: 3px solid #ccc;
  color: #ccc;
}

/* Estados del marcador */
.timeline-item.is-completed .marker-icon {
  background: v-bind(completedColor);
  border-color: v-bind(completedColor);
  color: white;
}

.timeline-item.is-active .marker-icon {
  background: v-bind(activeColor);
  border-color: v-bind(activeColor);
  color: white;
  box-shadow: 0 0 0 4px rgba(107, 32, 193, 0.2);
  animation: pulse 2s infinite;
}

.timeline-item.is-pending .marker-icon {
  background: white;
  border-color: v-bind(pendingColor);
  color: v-bind(pendingColor);
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 4px rgba(107, 32, 193, 0.2);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(107, 32, 193, 0.1);
  }
}

/* Línea conectora */
.timeline-line {
  position: absolute;
  left: 19px;
  top: 32px;
  bottom: -40px;
  width: 2px;
  background: #e0e0e0;
  transition: background 0.3s ease;
}

.timeline-line.is-completed {
  background: v-bind(completedColor);
}

/* Contenido */
.timeline-content {
  flex: 1;
  margin-left: 20px;
  padding: 8px 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #e0e0e0;
  transition: all 0.3s ease;
}

.timeline-item.is-completed .timeline-content {
  border-left-color: v-bind(completedColor);
}

.timeline-item.is-active .timeline-content {
  border-left-color: v-bind(activeColor);
  background: rgba(107, 32, 193, 0.05);
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.timeline-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.timeline-date {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
  margin-left: 10px;
}

.timeline-description {
  margin: 8px 0 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

/* Tags */
.timeline-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.timeline-tag {
  display: inline-block;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 500;
  color: white;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.timeline-custom {
  margin-top: 10px;
}

/* Hover effects */
.timeline-item:hover .timeline-content {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateX(2px);
}

/* Responsive */
@media (max-width: 768px) {
  .timeline-content {
    margin-left: 15px;
    padding: 8px 15px;
  }
  
  .timeline-title {
    font-size: 14px;
  }
  
  .timeline-description {
    font-size: 13px;
  }
}
</style>